import React, { useEffect, useMemo, useState } from 'react';
import { IColumn, IData, ISelectMethods, IRowSelection, IConfig } from '../interface';
import { Checkbox } from '@umu/umu-design';

const getRecordByKey = (data: IData[], value: string, key: string) => {
    return data.find((item: IData) => item[key] === value);
};

const useSelection = (config: IConfig, rowSelection?: IRowSelection) => {
    const { columns, data } = config;
    if (!rowSelection || !rowSelection.key) {
        return [columns];
    }
    const {
        preserveSelectedRowKeys,
        onSelectAll = () => {},
        onSelectionChange = () => {},
        key: rowKey,
    } = rowSelection || {};
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>(preserveSelectedRowKeys || []);
    const keySet = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys]);

    useEffect(() => {
        if (preserveSelectedRowKeys) {
            setSelectedRowKeys(preserveSelectedRowKeys);
        }
    }, [preserveSelectedRowKeys]);

    const transformColumns = useMemo(() => {
        const recordKeys = data.map((item: IData) => item[rowKey]);
        const checkedCurrentAll = recordKeys.every((recordKey: string) => keySet.has(recordKey));
        const checkedCurrentSome = recordKeys.some((recordKey: string) => keySet.has(recordKey));
        const setSelectedKeys = (keys: string[], method: ISelectMethods) => {
            const newKeySet = new Set(keys);
            const newSelectedRowKeys = Array.from(newKeySet);
            setSelectedRowKeys(newSelectedRowKeys);
            newKeySet.forEach((key) => {
                keySet.add(key);
            });

            const records: IData[] = [];
            const allKeys = Array.from(keySet);
            allKeys.forEach((key) => {
                const record = getRecordByKey(data, key, rowKey);
                if (record !== undefined) {
                    records.push(record);
                }
            });

            onSelectionChange?.(allKeys, records, { type: method });
        };

        const onSelectAllChange = (_e: any) => {
            const changeKeys: string[] = [];
            const checkedCurrentAll = recordKeys.every((recordKey: string) => keySet.has(recordKey));

            if (checkedCurrentAll) {
                recordKeys.forEach((key: string) => {
                    keySet.delete(key);
                    changeKeys.push(key);
                });
            } else {
                recordKeys.forEach((key: string) => {
                    if (!keySet.has(key)) {
                        keySet.add(key);
                        changeKeys.push(key);
                    }
                });
            }

            const keys = Array.from(keySet);

            onSelectAll?.(
                !checkedCurrentAll,
                keys,
                changeKeys,
                changeKeys.map((key: string) => getRecordByKey(data, key, rowKey) || {}),
            );

            setSelectedKeys(keys, ISelectMethods.ALL);
        };

        let title: JSX.Element | string | React.ReactNode;
        const isCheckedAll = !!recordKeys.length && checkedCurrentAll;
        const isIndeterminate = !!recordKeys.length && !checkedCurrentAll && checkedCurrentSome;
        title = <Checkbox checked={isCheckedAll} onChange={onSelectAllChange} indeterminate={isIndeterminate}></Checkbox>;

        const renderColumnTitle = () => {
            if (!rowSelection?.columnTitle) {
                return title;
            }
            if (typeof rowSelection.columnTitle === 'function') {
                return rowSelection.columnTitle();
            }
            return rowSelection.columnTitle;
        };

        const renderCell = (record: IData, _rowIndex?: number, _index?: number) => {
            const checked = keySet.has(record[rowKey]);
            const onValueChange = (e: any) => {
                const targetChecked = e.target.checked;
                const selectKey = record[rowKey];
                if (targetChecked) {
                    keySet.add(selectKey);
                } else {
                    keySet.delete(selectKey);
                }
                setSelectedKeys(Array.from(keySet), ISelectMethods.MULTIPLE);
            };
            return <Checkbox checked={checked} onChange={onValueChange}></Checkbox>;
        };

        const renderSelectionCell = (record: IData, _rowIndex?: number, _index?: number) => {
            if (rowSelection.customizeRenderCell) {
                return rowSelection.customizeRenderCell(record, _rowIndex, _index);
            } else {
                return renderCell(record);
            }
        };

        const selectionColumn: IColumn = {
            width: rowSelection.columnWidth || '100px',
            name: renderColumnTitle() as IColumn['name'],
            dataIndex: rowKey,
            render: renderSelectionCell,
        };

        return [selectionColumn, ...columns];
    }, [preserveSelectedRowKeys, selectedRowKeys, data, rowKey, keySet, columns]);

    return [transformColumns];
};

export { useSelection };
